import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Json } from '@/lib/database.types';

/**
 * סקרים ותיבת משוב (PRD 5.10.5).
 *
 * שתיהן עוברות ב RPC, כי אנונימיות היא החלטה של השרת ולא של הדפדפן:
 * מי ששולח בוחר אנונימי, והשרת הוא זה שלא שומר את השם.
 */

export type AnswerKind = 'choice' | 'multi' | 'scale' | 'text';

export const ANSWER_KIND_LABEL: Record<AnswerKind, string> = {
  scale: 'דירוג 1 עד 5',
  choice: 'בחירה אחת',
  multi: 'בחירה מרובה',
  text: 'טקסט חופשי',
};

export type SurveyQuestion = {
  id: string;
  prompt: string;
  answer_kind: AnswerKind;
  options: string[];
  sort_order: number;
  /** כותרת החלק שהשאלה שייכת אליו. null = בלי חלק. */
  section: string | null;
  is_required: boolean;
  /** בדירוג בלבד: מוסיף "לא רלוונטי" (מיגרציה 085). */
  allow_na: boolean;
};

/** הערך שנשמר כשנבחר "לא רלוונטי" בשאלת דירוג. */
export const NA = 'na';

export type Survey = {
  survey_id: string;
  title: string;
  description: string | null;
  is_anonymous: boolean;
  ends_at: string | null;
  answered: boolean;
  questions: SurveyQuestion[];
};

/** תשובות הסקר, בצורה ש PostgREST יודע לשלוח כ jsonb. */
export type SurveyAnswers = Record<string, Json>;

/**
 * תוצאות מסוכמות (מיגרציה 085).
 *
 * `percent` מחושב מול `answered`, כלומר מול מי שבאמת ענה על
 * השאלה — לא מול כל מי שהגיש את הסקר. `skipped` ו `na` חוזרים
 * בנפרד כדי שיהיה אפשר לראות אותם ולא רק לחסר אותם.
 */
export type ResultSlice = { label: string; count: number; percent: number };

export type QuestionResult = {
  id: string;
  prompt: string;
  answer_kind: AnswerKind;
  section: string | null;
  /** למי שמנתח בלבד. my_surveys לא מחזיר אותה. */
  analysis_note: string | null;
  is_required: boolean;
  allow_na: boolean;
  answered: number;
  skipped: number;
  na: number;
  average: number | null;
  distribution: ResultSlice[];
  texts: { text: string; branch: string | null; name: string | null }[];
};

export type SurveyResults = {
  id: string;
  title: string;
  description: string | null;
  analysis_note: string | null;
  is_anonymous: boolean;
  starts_at: string;
  ends_at: string | null;
  /** כמה עובדים פעילים יש בסך הכל. */
  total: number;
  /** כמה מהם הגישו. */
  participated: number;
  responses: number;
  questions: QuestionResult[];
};

export type FeedbackItem = {
  id: string;
  body: string;
  status: 'new' | 'read' | 'handled';
  created_at: string;
  employee_id: string | null;
  feedback_categories: { name: string } | null;
  employees: { first_name: string; last_name: string } | null;
  branches: { name: string } | null;
};

export const SURVEYS_KEY = ['surveys'] as const;

export function useMySurveys() {
  return useQuery({
    queryKey: SURVEYS_KEY,
    queryFn: async (): Promise<Survey[]> => {
      const { data, error } = await supabase.rpc('my_surveys');
      if (error) throw error;
      return (data as Survey[] | null) ?? [];
    },
  });
}

export function useSubmitSurvey() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { surveyId: string; answers: SurveyAnswers }) => {
      const { error } = await supabase.rpc('submit_survey', {
        p_survey: input.surveyId,
        p_answers: input.answers,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SURVEYS_KEY });
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useSurveyResults(surveyId: string | null) {
  return useQuery({
    queryKey: [...SURVEYS_KEY, 'results', surveyId],
    enabled: Boolean(surveyId),
    queryFn: async (): Promise<SurveyResults | null> => {
      const { data, error } = await supabase.rpc('survey_results', {
        p_survey: surveyId as string,
      });
      if (error) throw error;
      return data as SurveyResults | null;
    },
  });
}

export function useFeedbackCategories() {
  return useQuery({
    queryKey: ['feedback-categories'],
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('feedback_categories')
        .select('id, name')
        .order('name');
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useSubmitFeedback() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { body: string; categoryId: string | null; anonymous: boolean }) => {
      const { error } = await supabase.rpc('submit_feedback', {
        p_body: input.body,
        p_category: input.categoryId ?? undefined,
        p_anonymous: input.anonymous,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['feedback'] });
    },
  });
}

/** תיבת המשוב, למי שיש לו feedback.read. */
export function useFeedback() {
  return useQuery({
    queryKey: ['feedback'],
    queryFn: async (): Promise<FeedbackItem[]> => {
      const { data, error } = await supabase
        .from('feedback_items')
        .select(
          `id, body, status, created_at, employee_id,
           feedback_categories ( name ),
           employees ( first_name, last_name ),
           branches ( name )`,
        )
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as FeedbackItem[];
    },
  });
}

export function useFeedbackMutations() {
  const queryClient = useQueryClient();
  return {
    setStatus: useMutation({
      mutationFn: async (input: { id: string; status: FeedbackItem['status'] }) => {
        const { error } = await supabase
          .from('feedback_items')
          .update({ status: input.status })
          .eq('id', input.id);
        if (error) throw error;
      },
      onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['feedback'] }),
    }),
  };
}

export const feedbackStatusLabel: Record<FeedbackItem['status'], string> = {
  new: 'חדש',
  read: 'נקרא',
  handled: 'טופל',
};

/** האם כל השאלות נענו. שאלת טקסט ריקה נחשבת לא נענתה. */
export function isComplete(questions: SurveyQuestion[], answers: SurveyAnswers): boolean {
  // שאלה שאינה חובה אינה חוסמת את השליחה. בלי זה "משהו אחרון
  // שתרצה לספר" הופך לשדה חובה, ומי שאין לו מה לכתוב ממציא.
  return questions
    .filter((question) => question.is_required)
    .every((question) => {
      const value = answers[question.id];
      if (question.answer_kind === 'multi') return Array.isArray(value) && value.length > 0;
      if (typeof value === 'string') return value.trim().length > 0;
      return value !== undefined && value !== null;
    });
}

/** הקבוצות של הסקר לפי החלקים, בסדר שבו השאלות הוגדרו. */
export function groupBySection(
  questions: SurveyQuestion[],
): { section: string | null; questions: SurveyQuestion[] }[] {
  const groups: { section: string | null; questions: SurveyQuestion[] }[] = [];
  for (const question of [...questions].sort((a, b) => a.sort_order - b.sort_order)) {
    const last = groups.at(-1);
    // שאלות עוקבות עם אותו שם חלק הן חלק אחד. שם שחוזר אחרי חלק
    // אחר פותח בלוק חדש, כי הסדר הוא מה שהמגדיר התכוון אליו.
    if (last && last.section === question.section) last.questions.push(question);
    else groups.push({ section: question.section, questions: [question] });
  }
  return groups;
}
